import { z } from 'zod';
import { ACHIEVEMENT_ICON_MAX_BYTES, ACHIEVEMENT_ICON_MIN_PX, ACHIEVEMENT_ICON_PX, PlayCatalog, PlayIconPath, } from './play.js';
/**
 * Achievement icons on their way up (`docs/achievements-plan.md` §5.4): **one implementation** of
 * what every pusher does before it `PUT`s `boardflight.play.json` — agentd's deploy and
 * `boardflight catalog push` today, the MCP tools and Unity's *Build & Upload* after them.
 *
 * In the repo an icon is a path beside the catalog. On the server it is an uploaded file of the
 * game's, `{ media, sha256 }`. So each path is read and hashed; the server is asked which files it
 * already holds for this game, by digest; the ones it lacks go up as store media do — create,
 * presigned `PUT`, finalize — and the catalog is sent naming each by id. An icon that has not
 * changed is never sent twice.
 *
 * Everything outside is injected — how a path becomes bytes, how a call reaches the API with the
 * caller's credential, and the presigned upload, which carries no credential at all — so the
 * protocol is written once and each runtime keeps its own client.
 *
 * **What it never does is stop a catalog.** A file that is not there, a picture the server refuses
 * (`icon_not_square`, `icon_too_small`) or a game without the feature is a sentence for the caller
 * to say, in the server's own words where the server said it, and that achievement keeps its path:
 * the schema accepts one, the layer shows no icon for it, and the next push tries again.
 */
/**
 * `POST /v1/apps/:appId/achievement-icons/import` (§10.2, §13.6): an icon by the `https` address a
 * generator returned, which the API fetches itself — for an agent that holds a URL and no file
 * system to `PUT` from. The same checks as an upload's finalize, after a size cap on the way in.
 */
export const ImportAchievementIconRequest = z.object({
    url: z
        .url('An address, like https://…/icon.png.')
        .max(2048)
        .refine((value) => value.startsWith('https://'), 'Only an https address is fetched.'),
});
const ascii = (bytes, at, length) => String.fromCharCode(...bytes.subarray(at, at + length));
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
/**
 * A PNG's `IHDR` or a WebP's `VP8 `, `VP8L` or `VP8X` chunk, read for the picture's type and size.
 * Null for anything else, and for a header too short to say — no dependency, no decoder, and so
 * nothing about the rest of the file is believed: the server measures it again at finalize.
 */
export function readIconHeader(bytes) {
    if (bytes.length >= 24 && PNG_SIGNATURE.every((byte, index) => bytes[index] === byte) && ascii(bytes, 12, 4) === 'IHDR') {
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        return { contentType: 'image/png', width: view.getUint32(16), height: view.getUint32(20) };
    }
    if (bytes.length >= 30 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') {
        const chunk = ascii(bytes, 12, 4);
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        if (chunk === 'VP8 ') {
            // A key frame's start code, then 14-bit sizes with two bits of scale above them.
            if (bytes[23] !== 0x9d || bytes[24] !== 0x01 || bytes[25] !== 0x2a)
                return null;
            return { contentType: 'image/webp', width: view.getUint16(26, true) & 0x3fff, height: view.getUint16(28, true) & 0x3fff };
        }
        if (chunk === 'VP8L') {
            if (bytes[20] !== 0x2f)
                return null;
            const bits = view.getUint32(21, true);
            return { contentType: 'image/webp', width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1 };
        }
        if (chunk === 'VP8X') {
            const width = 1 + (bytes[24] | (bytes[25] << 8) | (bytes[26] << 16));
            const height = 1 + (bytes[27] | (bytes[28] << 8) | (bytes[29] << 16));
            return { contentType: 'image/webp', width, height };
        }
    }
    return null;
}
/**
 * What is wrong with an icon file before anybody uploads it, one sentence each — `play_check`'s
 * icon lines. The server measures again and has the last word; these are the ones it would say.
 */
export function iconFileProblems(path, bytes) {
    if (bytes === null)
        return [`${path} is not there.`];
    const header = readIconHeader(bytes);
    if (!header)
        return [`${path} is not a PNG or WebP file.`];
    const problems = [];
    const size = `${header.width} × ${header.height}`;
    const wanted = `${ACHIEVEMENT_ICON_PX} × ${ACHIEVEMENT_ICON_PX}`;
    if (header.width !== header.height) {
        problems.push(`${path} is ${size}; an icon is square, ${wanted}, and the server refuses one that is not.`);
    }
    else if (header.width < ACHIEVEMENT_ICON_MIN_PX) {
        problems.push(`${path} is ${size}; the server refuses an icon under ${ACHIEVEMENT_ICON_MIN_PX} px a side. Make it ${wanted}.`);
    }
    else if (header.width < ACHIEVEMENT_ICON_PX) {
        problems.push(`${path} is ${size}; an icon is ${wanted}.`);
    }
    if (bytes.length > ACHIEVEMENT_ICON_MAX_BYTES) {
        problems.push(`${path} is ${Math.ceil(bytes.length / 1024)} KB; an icon is at most ${ACHIEVEMENT_ICON_MAX_BYTES / 1024} KB.`);
    }
    return problems;
}
/** A digest as the catalog names one: 64 lowercase hex. */
export async function playIconSha256(bytes) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
/** A refusal as the API words it: its sentence, its code, and anything it named. */
async function refusalOf(response) {
    const text = await response.text().catch(() => '');
    try {
        const body = JSON.parse(text);
        if (typeof body.error?.message === 'string' && body.error.message) {
            return {
                message: body.error.message,
                code: typeof body.error.code === 'string' ? body.error.code : null,
                details: body.error.details,
            };
        }
    }
    catch {
        // Not JSON; the status is all there is.
    }
    return { message: `BoardFlight answered HTTP ${response.status}.`, code: null, details: undefined };
}
/** The refusal's own sentence, or the status when the body has none. */
async function refusal(response) {
    return (await refusalOf(response)).message;
}
const describeFailure = (failure) => `Could not reach BoardFlight: ${failure instanceof Error ? failure.message : String(failure)}`;
/**
 * Upload the icons a catalog names by path that the server does not already hold for the game,
 * and hand back the catalog to send.
 *
 * A catalog the schema refuses is handed back untouched with nothing uploaded: the `PUT` will refuse
 * it in sentences that name every entry, and an icon uploaded for a catalog that does not save is
 * an upload for nothing. Two achievements naming one file share one upload.
 */
export async function pushPlayIcons(catalog, transport) {
    const raw = (catalog && typeof catalog === 'object' ? catalog : {});
    const result = { catalog: raw, uploaded: [], kept: [], problems: [], unavailable: null };
    if (!PlayCatalog.safeParse(raw).success)
        return result;
    const entries = (raw.achievements ?? {});
    const named = Object.entries(entries).filter((pair) => typeof pair[1].icon === 'string' && PlayIconPath.safeParse(pair[1].icon).success);
    if (named.length === 0)
        return result;
    const ready = [];
    for (const [key, entry] of named) {
        const path = entry.icon;
        let bytes;
        try {
            bytes = await transport.readFile(path);
        }
        catch {
            bytes = null;
        }
        if (bytes === null) {
            result.problems.push(`Achievement ${key}: ${path} is not there, so it has no icon on BoardFlight yet.`);
            continue;
        }
        const header = readIconHeader(bytes);
        if (!header) {
            result.problems.push(`Achievement ${key}: ${path} is not a PNG or WebP file, so it was not uploaded.`);
            continue;
        }
        ready.push({ key, path, bytes, header, sha256: await playIconSha256(bytes) });
    }
    if (ready.length === 0)
        return result;
    const held = new Map();
    try {
        const listed = await transport.api(`/v1/apps/${transport.appId}/achievement-icons`, { method: 'GET' });
        if (!listed.ok) {
            const message = await refusal(listed);
            if (listed.status === 404)
                result.unavailable = message;
            else
                result.problems.push(`No icon was uploaded: ${message}`);
            return result;
        }
        const body = (await listed.json());
        for (const icon of body.icons ?? []) {
            if (icon && typeof icon.sha256 === 'string' && typeof icon.mediaId === 'string')
                held.set(icon.sha256, icon.mediaId);
        }
    }
    catch (failure) {
        result.problems.push(`No icon was uploaded: ${describeFailure(failure)}`);
        return result;
    }
    const rewritten = { ...entries };
    for (const icon of ready) {
        let media = held.get(icon.sha256);
        if (media) {
            result.kept.push(icon.key);
        }
        else {
            const sent = await uploadIcon(icon, transport);
            if ('problem' in sent) {
                result.problems.push(`Achievement ${icon.key}: ${icon.path} was not uploaded — ${sent.problem}`);
                continue;
            }
            media = sent.mediaId;
            held.set(icon.sha256, media);
            result.uploaded.push(icon.key);
        }
        rewritten[icon.key] = { ...entries[icon.key], icon: { media, sha256: icon.sha256 } };
    }
    result.catalog = { ...raw, achievements: rewritten };
    return result;
}
/** One file, the three calls store media makes. */
async function uploadIcon(icon, transport) {
    try {
        const created = await transport.api(`/v1/apps/${transport.appId}/media`, {
            method: 'POST',
            body: JSON.stringify({
                kind: 'achievement',
                contentType: icon.header.contentType,
                size: icon.bytes.length,
                width: icon.header.width,
                height: icon.header.height,
                sha256: icon.sha256,
            }),
        });
        if (!created.ok) {
            const refused = await refusalOf(created);
            // The game already holds this file — listed after the list was read, or by another pusher
            // in between. The server names it, and naming it is all that was wanted.
            const held = refused.details?.mediaId;
            if (created.status === 409 && refused.code === 'icon_exists' && typeof held === 'string')
                return { mediaId: held };
            return { problem: refused.message };
        }
        const target = (await created.json());
        const put = await transport.upload(target.uploadUrl, {
            method: target.method ?? 'PUT',
            headers: target.headers ?? {},
            body: icon.bytes,
        });
        if (!put.ok)
            return { problem: `the upload was refused with HTTP ${put.status}.` };
        const finalized = await transport.api(`/v1/apps/${transport.appId}/media/${target.mediaId}/finalize`, { method: 'POST' });
        if (!finalized.ok)
            return { problem: await refusal(finalized) };
        return { mediaId: target.mediaId };
    }
    catch (failure) {
        return { problem: describeFailure(failure) };
    }
}
/** The lines a pusher says about its icons, beside the catalog's own answer. */
export function describePlayIconPush(push) {
    const lines = [];
    if (push.uploaded.length > 0)
        lines.push(`Uploaded ${push.uploaded.length} achievement icon(s): ${push.uploaded.join(', ')}.`);
    if (push.unavailable)
        lines.push(`No icon was uploaded: ${push.unavailable}`);
    return [...lines, ...push.problems];
}
//# sourceMappingURL=play-icons.js.map